import {
	addDays,
	endOfDay,
	endOfMonth,
	endOfWeek,
	endOfYear,
	format,
	startOfDay,
	startOfMonth,
	startOfWeek,
	startOfYear,
	subDays,
} from 'date-fns';
import type {
	CalendarEventType,
	CalendarViewType,
} from '@/components/event-calendar/types';

export interface GoogleCalendarSource {
	id: string;
	name: string;
	busyOnly: boolean;
}

export interface GoogleCalendarEventDTO {
	id: string;
	calendarId: string;
	title: string;
	start: string;
	end: string;
	allDay: boolean;
	busy?: boolean;
	location?: string;
	htmlLink?: string;
	// Set on appointments the AI Employee booked for a customer
	_customer?: string;
}

// Padded by a week so the leading/trailing days a month or week grid shows
// (whichever day the week starts on) are covered too.
export const getVisibleRange = (
	currentDate: Date,
	currentView: CalendarViewType
) => {
	switch (currentView) {
		case 'year':
			return {
				start: startOfYear(currentDate),
				end: endOfYear(currentDate),
			};
		case 'month':
			return {
				start: subDays(startOfMonth(currentDate), 7),
				end: addDays(endOfMonth(currentDate), 7),
			};
		case 'week':
			return {
				start: subDays(startOfWeek(currentDate), 7),
				end: addDays(endOfWeek(currentDate), 7),
			};
		default:
			return {
				start: startOfDay(currentDate),
				end: endOfDay(currentDate),
			};
	}
};

export const toCalendarEvent = (
	item: GoogleCalendarEventDTO,
	calendar: GoogleCalendarSource
): CalendarEventType => {
	let startDate: Date;
	let endDate: Date;
	// The calendar treats 00:00 - 23:59 as an all-day event
	let startTime = '00:00';
	let endTime = '23:59';

	if (item.allDay) {
		// Date-only values are calendar days, not instants: keep them local.
		// Google's all-day end date is exclusive.
		startDate = new Date(`${item.start}T00:00:00`);
		endDate = subDays(new Date(`${item.end}T00:00:00`), 1);
	} else {
		startDate = new Date(item.start);
		endDate = new Date(item.end);
		// An event that ends on the stroke of midnight belongs to the day before
		if (format(endDate, 'HH:mm') === '00:00' && endDate > startDate) {
			endDate = new Date(endDate.getTime() - 60 * 1000);
		}
		startTime = format(startDate, 'HH:mm');
		endTime = format(endDate, 'HH:mm');
	}

	return {
		id: `google:${item.id}`,
		title: item.title,
		description: '',
		startDate,
		endDate,
		startTime,
		endTime,
		color: calendar.busyOnly ? 'google-busy' : 'google',
		isRepeating: false,
		isRepeatingInterval: 'daily' as const,
		isRepeatingExcludedDates: [],
		google: {
			calendarId: calendar.id,
			calendarName: calendar.name,
			busyOnly: calendar.busyOnly,
			allDay: item.allDay,
			start: item.start,
			end: item.end,
			location: item.location,
			htmlLink: item.htmlLink,
			_customer: item._customer,
		},
	};
};
